import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from "recharts";
import {
  Users, ClipboardList, Brain, Code2, AlertTriangle, TrendingDown,
  Activity, BookOpen, GraduationCap, FileText, Layers, ChevronRight,
} from "lucide-react";
import { fetchBatchAnalytics } from "../../utils/smartLearning";
import {
  AppCard, AppCardContent, AppBadge, StatsCard, StatsGrid, EmptyState,
  SearchInput, SelectControl, PanelButton, cn,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import { useTokenColors } from "../../utils/themeColor";

/* ── Performance bands ───────────────────────────────────────────────────────
   Every percentage on this screen lands in one of three bands, and the band is
   drawn three different ways. The token family differs per use and they are not
   interchangeable: a solid bar takes the family itself, ink takes its `-fg`
   half, and a tinted pill pairs `-soft` with that same ink. */
const toneOf = (v) => (v == null ? null : v >= 75 ? "good" : v >= 50 ? "mid" : "low");

const TONE_INK_CLASSES = {
  good: "tw-text-success-fg",
  mid: "tw-text-warning-fg",
  low: "tw-text-danger-fg",
};
const TONE_FILL_CLASSES = {
  good: "tw-bg-success",
  mid: "tw-bg-warning",
  low: "tw-bg-danger",
};
const TONE_CHIP_CLASSES = {
  good: "tw-bg-success-soft tw-text-success-fg",
  mid: "tw-bg-warning-soft tw-text-warning-fg",
  low: "tw-bg-danger-soft tw-text-danger-fg",
};

const toneInk = (v) => TONE_INK_CLASSES[toneOf(v)] || "";
const fmtPct = (v) => (v == null ? "—" : `${v}%`);

/* Recharts sets fill and stroke as SVG PRESENTATION ATTRIBUTES, and var() does
   not resolve in an attribute — so a chart has to READ its tokens rather than
   reference them. `useTokenColors` re-reads them when the theme or the
   institute's brand moves underneath. */
const CHART_TOKENS = {
  tick: "--text-secondary",
  cursor: "--elevate-1",
  low: "--danger",
  mid: "--warning",
  high: "--success",
  lang1: "--chart-1",
  lang2: "--chart-2",
  lang3: "--chart-3",
  lang4: "--chart-4",
  lang5: "--chart-5",
  lang6: "--chart-6",
};
const LANG_TOKEN_KEYS = ["lang1", "lang2", "lang3", "lang4", "lang5", "lang6"];

/* The tooltip is the one part of a Recharts chart that is an HTML div, so var()
   resolves here and these stay references. */
const tooltipStyle = {
  background: "var(--card-bg)",
  border: "1px solid var(--border-primary)",
  borderRadius: "10px",
  fontSize: "12px",
  padding: "6px 10px",
  color: "var(--text-primary)",
};

const PAGE_SHELL = "tw-mt-1 tw-flex tw-flex-col tw-gap-[18px]";

/* ── A labelled horizontal bar (submission rate / accuracy / solve rate) ── */
function MetricBar({ value, label, sub }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
        <span className="tw-truncate tw-text-[0.76rem] tw-text-content-muted">{label}</span>
        {sub != null && (
          <span className="tw-shrink-0 tw-text-[0.72rem] tw-font-semibold tw-text-content">{sub}</span>
        )}
      </div>
      <div className="tw-h-[7px] tw-overflow-hidden tw-rounded-full tw-bg-surface-hover">
        <div
          className={cn(
            "tw-h-full tw-rounded-full tw-transition-[width] tw-duration-[400ms] tw-ease-[ease]",
            TONE_FILL_CLASSES[toneOf(value)] || "tw-bg-primary",
          )}
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
}

/* ── A pill carrying one number, tinted by the band it falls in ── */
function StatChip({ value, children }) {
  return (
    <span
      className={cn(
        "tw-whitespace-nowrap tw-rounded-full tw-px-[9px] tw-py-0.5 tw-text-[0.72rem] tw-font-semibold",
        TONE_CHIP_CLASSES[toneOf(value)] || "tw-bg-surface-hover tw-text-content-muted",
      )}
    >
      {children}
    </span>
  );
}

/* ── Section card shell ── */
function Section({ icon: Icon, title, count, action, children, className = "" }) {
  return (
    <AppCard className={className}>
      <AppCardContent>
        <div className="tw-mb-3.5 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 max-[768px]:tw-items-start">
          <h6 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-[0.95rem] tw-font-bold tw-text-content">
            {Icon && <Icon size={17} className="tw-text-primary" aria-hidden="true" />}
            {title}
            {count != null && (
              <span className="tw-rounded-full tw-bg-surface-hover tw-px-2 tw-py-px tw-text-[0.72rem] tw-font-semibold tw-text-content-muted">
                {count}
              </span>
            )}
          </h6>
          {action}
        </div>
        {children}
      </AppCardContent>
    </AppCard>
  );
}

/**
 * One cell of the student list.
 *
 * On desktop the row is a flex line whose cells share the column widths the head
 * row uses. Below 768px the row becomes a two-column card and the head is gone,
 * so each cell prints its own column name above the value.
 */
function StudentCell({ label, wide = false, tone = "", children }) {
  return (
    <span
      className={cn(
        "tw-min-w-0 max-[768px]:tw-flex-none",
        wide ? "tw-flex-[2_1_120px]" : "tw-flex-[1_1_0]",
        wide && "tw-font-semibold max-[768px]:tw-col-span-full max-[768px]:tw-text-[0.92rem]",
        label && "max-[768px]:tw-flex max-[768px]:tw-flex-col max-[768px]:tw-gap-0.5 max-[768px]:tw-font-semibold",
        tone,
      )}
    >
      {label && (
        <span className="tw-hidden tw-text-[0.64rem] tw-font-medium tw-uppercase tw-tracking-[0.03em] tw-text-content-muted max-[768px]:tw-block">
          {label}
        </span>
      )}
      {children}
    </span>
  );
}

/* ── Loading skeleton mirroring the layout ── */
function AnalyticsSkeleton() {
  return (
    <div className={PAGE_SHELL}>
      <StatsGrid>
        {Array.from({ length: 4 }).map((_, i) => (
          <StatsCard key={i} loading />
        ))}
      </StatsGrid>
      <Shimmer height={180} />
      <Shimmer height={240} />
    </div>
  );
}

const STATUS_META = {
  at_risk: { label: "At risk", props: { variant: "destructive" } },
  ok: { label: "On track", props: { variant: "secondary" } },
  good: { label: "Strong", props: { color: "emerald" } },
};

/**
 * BatchAnalytics — instructor batch insights.
 * Topic/module performance from Smart Sessions, assignments, quizzes,
 * Learn-with-AI and (when enabled) coding, plus a per-student breakdown.
 */
const BatchAnalytics = ({ batchId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // student table controls
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("status");

  const chart = useTokenColors(CHART_TOKENS);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchBatchAnalytics(batchId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || "Failed to load analytics."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [batchId]);

  const kpiTiles = useMemo(() => {
    if (!data) return [];
    const k = data.kpis;
    const tiles = [
      { title: "Students", value: k.students, icon: Users, iconColor: "blue", gradient: "blue" },
      { title: "Avg Progress", value: fmtPct(k.avgProgress), icon: Activity, iconColor: "violet", gradient: "violet" },
    ];
    if (k.assignmentSubmissionRate != null)
      tiles.push({ title: "Assignment Submissions", value: fmtPct(k.assignmentSubmissionRate), icon: ClipboardList, iconColor: "cyan", gradient: "cyan" });
    if (k.avgQuizScore != null)
      tiles.push({ title: "Avg Quiz Score", value: fmtPct(k.avgQuizScore), subtitle: k.quizPassRate != null ? `${k.quizPassRate}% pass rate` : undefined, icon: GraduationCap, iconColor: "emerald", gradient: "emerald" });
    if (data.codingEnabled && k.avgCodingSolved != null)
      tiles.push({ title: "Avg Problems Solved", value: k.avgCodingSolved, icon: Code2, iconColor: "amber", gradient: "amber" });
    tiles.push({ title: "Needs Attention", value: k.atRiskCount, subtitle: k.atRiskCount ? "students at risk" : "all on track", icon: AlertTriangle, iconColor: "rose", gradient: "rose" });
    return tiles;
  }, [data]);

  const students = useMemo(() => {
    if (!data?.students) return [];
    const q = query.trim().toLowerCase();
    let rows = q ? data.students.filter((s) => s.name.toLowerCase().includes(q)) : [...data.students];
    const rank = { at_risk: 0, ok: 1, good: 2 };
    rows.sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "quiz") return (b.quizAvg ?? -1) - (a.quizAvg ?? -1);
      if (sortBy === "progress") return b.avgProgress - a.avgProgress;
      return (rank[a.status] - rank[b.status]) || a.name.localeCompare(b.name); // status: at-risk first
    });
    return rows;
  }, [data, query, sortBy]);

  if (loading) return <AnalyticsSkeleton />;
  if (error) {
    return (
      <div className={PAGE_SHELL}>
        <EmptyState icon={AlertTriangle} title="Couldn’t load analytics" description={error} />
      </div>
    );
  }
  if (!data) return null;

  const { kpis, modules, weakestTopics, assignments, quiz, learnWithAI, coding, codingEnabled } = data;

  const isEmpty =
    !modules.length && !assignments.length && !quiz.hasData && !learnWithAI.hasData && !(coding && coding.hasData);

  if (isEmpty) {
    return (
      <div className={PAGE_SHELL}>
        <EmptyState
          icon={BookOpen}
          title="No analytics yet"
          description="Insights appear here once students start submitting assignments, attempting quizzes or completing sessions."
        />
      </div>
    );
  }

  const studentColumns = [
    "Student",
    "Progress",
    "Assignments",
    "Quiz",
    codingEnabled && "Coding",
    learnWithAI.hasData && "AI",
    "Status",
  ].filter(Boolean);

  return (
    <div className={PAGE_SHELL}>
      {/* ── KPI strip ── */}
      <StatsGrid>
        {kpiTiles.map((t) => (
          <StatsCard key={t.title} {...t} />
        ))}
      </StatsGrid>

      {/* ── Where students are struggling ── */}
      {weakestTopics.length > 0 && (
        <Section
          icon={TrendingDown}
          title="Where students are struggling"
          /* Widens and recolours the card's own left edge. The one border on this
             screen that needs no `tw-border-solid`: AppCard already sets the style. */
          className="tw-border-l-[3px] tw-border-l-warning"
        >
          <p className="-tw-mt-1.5 tw-mb-3.5 tw-text-[0.8rem] tw-text-content-muted">
            Topics with low submission, quiz scores or attendance — worth a recap.
          </p>
          <div className="tw-flex tw-flex-col tw-gap-2">
            {weakestTopics.map((t) => (
              <div
                className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5"
                key={t.sessionId}
              >
                <div className="tw-flex tw-min-w-0 tw-flex-col">
                  <span className="tw-text-[0.68rem] tw-uppercase tw-tracking-[0.03em] tw-text-content-muted">
                    {t.moduleTitle}
                  </span>
                  <span className="tw-truncate tw-text-[0.85rem] tw-font-semibold tw-text-content">{t.title}</span>
                </div>
                <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-justify-end tw-gap-1.5">
                  {t.quiz?.avgPercent != null && (
                    <StatChip value={t.quiz.avgPercent}>Quiz {t.quiz.avgPercent}%</StatChip>
                  )}
                  {t.assignment && (
                    <StatChip value={t.assignment.submissionRate}>
                      {t.assignment.submitted}/{t.assignment.total} submitted
                    </StatChip>
                  )}
                  {t.status === "completed" && (
                    <StatChip value={t.attendanceRate}>Att {t.attendanceRate}%</StatChip>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── By Topic / Module ── */}
      {modules.length > 0 && (
        <Section icon={Layers} title="By Topic & Module">
          <div className="tw-flex tw-flex-col tw-gap-[18px]">
            {modules.map((m) => (
              <div key={m.moduleNumber}>
                <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2.5 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-pb-2.5">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-[0.88rem] tw-font-bold tw-text-content">
                    <span className="tw-rounded-[6px] tw-bg-primary/[0.12] tw-px-[7px] tw-py-0.5 tw-text-[0.68rem] tw-font-bold tw-text-primary">
                      M{m.moduleNumber}
                    </span>
                    {m.title}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2.5">
                    {m.needsAttentionCount > 0 && (
                      <AppBadge color="amber" size="sm">
                        <AlertTriangle size={11} aria-hidden="true" /> {m.needsAttentionCount} need attention
                      </AppBadge>
                    )}
                    <span
                      className={cn(
                        "tw-text-[0.76rem] tw-font-semibold",
                        toneInk(m.avgProgress) || "tw-text-content-muted",
                      )}
                    >
                      {m.avgProgress}% avg progress
                    </span>
                  </div>
                </div>
                <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(248px,1fr))] tw-gap-3 max-[768px]:tw-grid-cols-1">
                  {m.sessions.map((s) => (
                    <div
                      className={cn(
                        "tw-rounded tw-border tw-border-solid tw-px-3.5 tw-py-3",
                        s.needsAttention
                          ? "tw-border-warning/50 tw-bg-warning/[0.04]"
                          : "tw-border-line tw-bg-surface-raised",
                      )}
                      key={s.sessionId}
                    >
                      <div className="tw-mb-2.5 tw-flex tw-items-center tw-justify-between tw-gap-2">
                        <span className="tw-truncate tw-text-[0.82rem] tw-font-semibold tw-text-content" title={s.title}>
                          {s.title}
                        </span>
                        {s.needsAttention && (
                          <AlertTriangle size={14} className="tw-shrink-0 tw-text-warning" aria-hidden="true" />
                        )}
                      </div>
                      <div className="tw-flex tw-flex-col tw-gap-2.5">
                        {s.assignment ? (
                          <MetricBar
                            label="Assignments submitted"
                            sub={`${s.assignment.submitted}/${s.assignment.total}`}
                            value={s.assignment.submissionRate}
                          />
                        ) : null}
                        {s.quiz && s.quiz.avgPercent != null ? (
                          <MetricBar label="Quiz avg" sub={`${s.quiz.attempted} attempted`} value={s.quiz.avgPercent} />
                        ) : null}
                        {s.coding ? (
                          <MetricBar label="Coding solved" sub={`${s.coding.solved}/${s.coding.total}`} value={s.coding.solveRate} />
                        ) : null}
                        {!s.assignment && !s.quiz && !s.coding && (
                          <span className="tw-text-[0.76rem] tw-text-content-muted">
                            {s.documentCount > 0 ? `${s.documentCount} learning resource(s)` : "No graded activity"}
                            {s.status === "completed" ? ` • ${s.attendanceRate}% attended` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Assignments ── */}
      {assignments.length > 0 && (
        <Section icon={FileText} title="Assignments" count={assignments.length}>
          <div className="tw-flex tw-flex-col tw-gap-2">
            {assignments.map((a) => (
              <PanelButton
                className="max-[768px]:tw-flex-wrap"
                key={a.id}
                onClick={() => navigate(a.reviewHref)}
                title="Open submissions"
              >
                <div className="tw-flex tw-min-w-0 tw-flex-[1_1_40%] tw-flex-col tw-gap-0.5">
                  <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-[0.85rem] tw-font-semibold tw-text-content">
                    {a.title}
                    {a.needsAttention && (
                      <AlertTriangle size={13} className="tw-shrink-0 tw-text-warning" aria-hidden="true" />
                    )}
                  </span>
                  <span className="tw-text-[0.74rem] tw-text-content-muted">
                    {a.graded}/{a.submitted} graded
                    {a.avgMarksPct != null ? ` • avg ${a.avgMarksPct}%` : ""}
                  </span>
                </div>
                <div className="tw-min-w-[120px] tw-flex-[1_1_45%] max-[768px]:tw-order-3 max-[768px]:tw-min-w-0 max-[768px]:tw-basis-full">
                  <MetricBar label={`${a.submitted}/${a.total} submitted`} value={a.submissionRate} />
                </div>
                <ChevronRight
                  size={16}
                  className="tw-shrink-0 tw-text-content-muted max-[768px]:tw-hidden"
                  aria-hidden="true"
                />
              </PanelButton>
            ))}
          </div>
        </Section>
      )}

      {/* ── Quiz performance ── */}
      {quiz.hasData && (
        <Section icon={GraduationCap} title="Quiz Performance">
          <div className="tw-grid tw-grid-cols-2 tw-gap-[22px] max-[992px]:tw-grid-cols-1">
            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
              <div className="tw-flex tw-flex-wrap tw-gap-2.5">
                <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                  <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{quiz.avgPercent}%</span>
                  <span className="tw-text-[0.7rem] tw-text-content-muted">Avg score</span>
                </div>
                <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                  <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{quiz.passRate}%</span>
                  <span className="tw-text-[0.7rem] tw-text-content-muted">Pass rate</span>
                </div>
                <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                  <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{quiz.uniqueStudents}</span>
                  <span className="tw-text-[0.7rem] tw-text-content-muted">Students</span>
                </div>
              </div>
              <div className="tw-min-w-0">
                <span className="tw-mb-2 tw-block tw-text-[0.76rem] tw-font-semibold tw-text-content-muted">
                  Score distribution
                </span>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={quiz.scoreDistribution} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: chart.tick }} interval={0} angle={-35} textAnchor="end" height={42} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: chart.tick }} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: chart.cursor }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {quiz.scoreDistribution.map((d, i) => (
                        <Cell key={i} fill={i < 4 ? chart.low : i < 6 ? chart.mid : chart.high} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
              {quiz.weakCategories.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-2.5">
                  <span className="tw-mb-2 tw-block tw-text-[0.76rem] tw-font-semibold tw-text-content-muted">
                    Weakest topics (by accuracy)
                  </span>
                  {quiz.weakCategories.map((c) => (
                    <MetricBar key={c.name} label={c.name} sub={`${c.accuracy}% · ${c.totalAnswered} ans`} value={c.accuracy} />
                  ))}
                </div>
              )}
              {quiz.byQuiz.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <span className="tw-mb-2 tw-block tw-text-[0.76rem] tw-font-semibold tw-text-content-muted">By quiz</span>
                  {quiz.byQuiz.map((q) => (
                    <div
                      className="tw-grid tw-grid-cols-[1fr_auto_auto] tw-items-center tw-gap-2.5 tw-border-0 tw-border-b tw-border-dashed tw-border-line tw-py-[7px] tw-text-[0.8rem] last:tw-border-b-0"
                      key={q.id}
                    >
                      <span className="tw-truncate tw-font-medium tw-text-content" title={q.title}>{q.title}</span>
                      <span className="tw-whitespace-nowrap tw-text-[0.74rem] tw-text-content-muted">
                        {q.attempted}/{q.total}
                      </span>
                      <StatChip value={q.avgPercent}>{q.avgPercent}%</StatChip>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* ── Learn with AI + Coding (side by side on desktop) ── */}
      {(learnWithAI.hasData || (codingEnabled && coding && coding.hasData)) && (
        <div className="tw-grid tw-grid-cols-2 tw-items-start tw-gap-[18px] max-[992px]:tw-grid-cols-1">
          {learnWithAI.hasData && (
            <Section icon={Brain} title="Learn with AI">
              <div className="tw-flex tw-flex-wrap tw-gap-2.5">
                <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                  <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{learnWithAI.totalAttempts}</span>
                  <span className="tw-text-[0.7rem] tw-text-content-muted">Attempts</span>
                </div>
                <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                  <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{learnWithAI.uniqueStudents}</span>
                  <span className="tw-text-[0.7rem] tw-text-content-muted">Students</span>
                </div>
                <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                  <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{learnWithAI.questionsAttempted}</span>
                  <span className="tw-text-[0.7rem] tw-text-content-muted">Questions</span>
                </div>
                {learnWithAI.avgScore != null && (
                  <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                    <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{learnWithAI.avgScore}%</span>
                    <span className="tw-text-[0.7rem] tw-text-content-muted">Avg score</span>
                  </div>
                )}
              </div>
            </Section>
          )}

          {codingEnabled && coding && coding.hasData && (
            <Section icon={Code2} title="Coding Practice">
              <div className="tw-flex tw-flex-col tw-gap-3.5">
                <div className="tw-flex tw-flex-wrap tw-gap-2.5">
                  <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                    <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{coding.solvedCount}</span>
                    <span className="tw-text-[0.7rem] tw-text-content-muted">Solved</span>
                  </div>
                  <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                    <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{coding.uniqueStudents}</span>
                    <span className="tw-text-[0.7rem] tw-text-content-muted">Students</span>
                  </div>
                  <div className="tw-flex tw-min-w-[80px] tw-flex-[1_1_80px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-bg-surface-hover tw-px-3 tw-py-2.5">
                    <span className="tw-text-[1.15rem] tw-font-bold tw-leading-none tw-text-content">{coding.totalSubmissions}</span>
                    <span className="tw-text-[0.7rem] tw-text-content-muted">Submissions</span>
                  </div>
                </div>
                {coding.languages.length > 0 && (
                  <div className="tw-flex tw-items-center tw-gap-3.5 max-[480px]:tw-flex-col max-[480px]:tw-items-stretch">
                    <ResponsiveContainer width="100%" height={120}>
                      <PieChart>
                        <Pie data={coding.languages} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={30} outerRadius={50} paddingAngle={3} strokeWidth={0}>
                          {coding.languages.map((d, i) => (
                            <Cell key={i} fill={chart[LANG_TOKEN_KEYS[i % LANG_TOKEN_KEYS.length]]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-[5px] tw-p-0">
                      {coding.languages.slice(0, 5).map((l, i) => (
                        <li
                          key={l.name}
                          className="tw-flex tw-items-center tw-gap-[7px] tw-text-[0.78rem] tw-text-content-muted"
                        >
                          <span
                            className="tw-h-[9px] tw-w-[9px] tw-shrink-0 tw-rounded-full"
                            style={{ background: chart[LANG_TOKEN_KEYS[i % LANG_TOKEN_KEYS.length]] }}
                          />
                          {l.name}
                          <b className="tw-ml-auto tw-text-content">{l.count}</b>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {coding.byProblem.length > 0 && (
                  <div className="tw-flex tw-flex-col tw-gap-1.5">
                    <span className="tw-mb-2 tw-block tw-text-[0.76rem] tw-font-semibold tw-text-content-muted">By problem</span>
                    {coding.byProblem.slice(0, 6).map((p) => (
                      <div
                        className="tw-grid tw-grid-cols-[1fr_auto_auto] tw-items-center tw-gap-2.5 tw-border-0 tw-border-b tw-border-dashed tw-border-line tw-py-[7px] tw-text-[0.8rem] last:tw-border-b-0"
                        key={p.id}
                      >
                        <span className="tw-truncate tw-font-medium tw-text-content" title={p.title}>{p.title}</span>
                        <span className="tw-whitespace-nowrap tw-text-[0.74rem] tw-text-content-muted">
                          {p.solved}/{p.attempted} solved
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Section>
          )}
        </div>
      )}

      {/* ── Students ── */}
      {data.students.length > 0 && (
        <Section
          icon={Users}
          title="Student Performance"
          count={data.students.length}
          action={
            <div className="tw-flex tw-items-center tw-gap-2 max-[768px]:tw-w-full">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search student"
                className="tw-w-[190px] max-[768px]:tw-w-auto max-[768px]:tw-max-w-none max-[768px]:tw-flex-1"
              />
              <SelectControl
                value={sortBy}
                onChange={setSortBy}
                ariaLabel="Sort students"
                options={[
                  { value: "status", label: "At-risk first" },
                  { value: "name", label: "Name" },
                  { value: "quiz", label: "Quiz score" },
                  { value: "progress", label: "Progress" },
                ]}
              />
            </div>
          }
        >
          <div className="tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-2.5 tw-py-2 tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-[0.03em] tw-text-content-muted max-[768px]:tw-hidden">
              {studentColumns.map((label, i) => (
                <span key={label} className={cn("tw-min-w-0", i === 0 ? "tw-flex-[2_1_120px]" : "tw-flex-[1_1_0]")}>
                  {label}
                </span>
              ))}
            </div>
            {students.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.ok;
              return (
                <div
                  className={cn(
                    "tw-flex tw-items-center tw-gap-2.5 tw-px-2.5 tw-py-[11px] tw-text-[0.82rem] tw-text-content",
                    // Bottom rule between rows, none under the last. Below 768px the
                    // media-query utility is emitted last, so `tw-border` closes the
                    // card on all four sides — including the last one.
                    "tw-border-0 tw-border-b tw-border-solid tw-border-line last:tw-border-b-0",
                    "max-[768px]:tw-mb-2.5 max-[768px]:tw-grid max-[768px]:tw-grid-cols-2 max-[768px]:tw-gap-x-3.5",
                    "max-[768px]:tw-gap-y-2 max-[768px]:tw-rounded max-[768px]:tw-border",
                    "max-[768px]:tw-p-3 max-[768px]:last:tw-mb-0",
                    s.status === "at_risk" && "tw-bg-danger/[0.04]",
                  )}
                  key={s.studentId}
                >
                  <StudentCell wide>{s.name}</StudentCell>
                  <StudentCell label="Progress" tone={toneInk(s.avgProgress)}>{s.avgProgress}%</StudentCell>
                  <StudentCell label="Assignments">{s.assignmentsSubmitted}/{s.assignmentsTotal}</StudentCell>
                  <StudentCell label="Quiz" tone={toneInk(s.quizAvg)}>{fmtPct(s.quizAvg)}</StudentCell>
                  {codingEnabled && (
                    <StudentCell label="Coding">{s.codingSolved}{s.codingTotal ? `/${s.codingTotal}` : ""}</StudentCell>
                  )}
                  {learnWithAI.hasData && <StudentCell label="AI">{s.aiAttempts}</StudentCell>}
                  <StudentCell label="Status">
                    <AppBadge {...meta.props} size="sm">{meta.label}</AppBadge>
                  </StudentCell>
                </div>
              );
            })}
            {students.length === 0 && (
              <p className="tw-mb-1 tw-mt-3.5 tw-text-center tw-text-[0.82rem] tw-text-content-muted">
                No students match “{query}”.
              </p>
            )}
          </div>
        </Section>
      )}
    </div>
  );
};

export default BatchAnalytics;
